import { useEffect } from 'react';
import { useMap } from 'react-map-gl';
import type { MapSourceDataEvent } from 'mapbox-gl';
import type { ParcelaLiveState } from '../types';

/**
 * Inyecta el estado vivo de cada parcela como `feature-state` para que las
 * expresiones `paint` lean color/firmado/irregular sin re-fetch del GeoJSON.
 *
 * El source debe tener `promoteId="parcela_id"` para que el id coincida.
 *
 * Race condition: si `parcelasState` (Supabase) llega ANTES de que el GeoJSON
 * cargue (cache caliente, datos pequeños), `getSource('parcelas')` retorna
 * undefined y antes salíamos sin reintentar → semáforo nunca se inyectaba.
 * Ahora suscribimos `sourcedata` y aplicamos cuando `parcelas` queda lista.
 *
 * Source es siempre GeoJSON estático (no vector tiles); ver
 * packages/sig-viewer/src/data-sources.ts.
 */
export function useLiveParcelas(parcelas: ParcelaLiveState[] | undefined): void {
  const { current: map } = useMap();

  useEffect(() => {
    if (!map || !parcelas) return;
    const m = map.getMap();
    let cancelled = false;

    function applyState() {
      if (cancelled || !m.getSource('parcelas')) return;
      for (const p of parcelas!) {
        m.setFeatureState(
          { source: 'parcelas', id: p.parcela_id },
          {
            estado: p.estado,
            firmado: p.firmado,
            irregular: p.irregular,
            reunion_bucket: p.reunion_bucket ?? 'sin_reunion',
          },
        );
      }
    }

    // Camino feliz: source ya está cargado.
    if (m.getSource('parcelas') && m.isSourceLoaded('parcelas')) {
      applyState();
      return () => { cancelled = true; };
    }

    // Camino lento: esperamos a que el GeoJSON termine de descargar
    // (un único `sourcedata` con isSourceLoaded=true).
    const onSourceData = (e: MapSourceDataEvent) => {
      if (cancelled) return;
      if (e.sourceId === 'parcelas' && e.isSourceLoaded) {
        applyState();
        m.off('sourcedata', onSourceData);
      }
    };
    m.on('sourcedata', onSourceData);

    return () => {
      cancelled = true;
      m.off('sourcedata', onSourceData);
    };
  }, [map, parcelas]);
}
